import { useImmerReducer } from 'use-immer';
import { useState, createContext, useContext, useEffect, useRef } from 'react';

import { BODY_SHAPES } from 'productTemplate/enums/fitFeedback';
import {
  FitFeedbackFormActions,
  fitFeedbackFormReducer,
  IFitFeedbackFormState,
  INITIAL_APPAREL_FIT_FEEDBACK_FORM_STATE,
  INITIAL_SNEAKERS_FIT_FEEDBACK_FORM_STATE,
  isApparelFormState,
  isSneakersFormState,
} from 'productTemplate/state/fitFeedbackFormReducer';
import { getFitFeedbackApparelCategoryType } from 'productTemplate/utils/getFitFeedbackApparelCategoryType';
import { getHeightInches } from 'productTemplate/utils/getHeightForFitFeedback';
import { requestService } from 'shared/utils/services/requestService';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { triggerLoginBeforeAction } from 'store/slice/accountModalSlice';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowState } from 'productTemplate/hooks/useWindowState';
import { t } from 'localization/utils/t';

const TP = 'productTemplate.providers.FitFeedbackProvider';

export const FitFeedbackContext = createContext(null);
export const FitFeedbackFormStateContext = createContext(null);
export const FitFeedbackFormDispatchContext = createContext(null);

export const useFitFeedbackFormDispatch = () => useContext(FitFeedbackFormDispatchContext);
export const useFitFeedbackFormState = () => useContext(FitFeedbackFormStateContext);

export const FitFeedbackProvider = ({ children }) => {
  const [isFitFeedbackFormWindowVisible, setIsFitFeedbackFormWindowVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [fitFeedbackData, setFitFeedbackData] = useState([]);
  const { productCategory, productType, slug, name } = useStoreSelector(
    (state) => state.pdp.productTemplate,
  );
  const allowedApparelProductTypes = new Set(['bottoms', 'dresses', 'outerwear', 'tops']);
  const isAllowedApparelProductType = allowedApparelProductTypes.has(productType);
  const isAllowedApparelCategoryType = getFitFeedbackApparelCategoryType(productType, name);
  const { isAuthenticated, user } = useStoreSelector((state) => state.auth);
  const storeDispatch = useStoreDispatch();
  const isSneakers = productCategory === 'shoes';
  const initialFormState: IFitFeedbackFormState = isSneakers
    ? INITIAL_SNEAKERS_FIT_FEEDBACK_FORM_STATE
    : INITIAL_APPAREL_FIT_FEEDBACK_FORM_STATE;
  const [state, dispatch] = useImmerReducer(fitFeedbackFormReducer, initialFormState);
  const openFitFeedbackForm = useRef(false);

  const {
    windowState: fitWindowState,
    handleLinkClick: handleFitClick,
    handleLinkClose: handleFitClose,
    handleLinkMouseEnter: handleFitMouseEnter,
    handleLinkMouseLeave: handleFitMouseLeave,
  } = useWindowState('fit');

  const isApparelFitFeedbackEnabled =
    productCategory === 'clothing' && isAllowedApparelProductType && isAllowedApparelCategoryType;

  const getHasUserSubmittedFitFeedback = async () => {
    try {
      const res = await requestService({
        url: `/web-api/fit-feedback/get-fit-feedback-for-user?productTemplateSlug=${slug}`,
        method: 'GET',
      });

      return !!res?.fitFeedback;
    } catch {
      return false;
    }
  };

  const handleOpenFitFeedbackForm = async () => {
    const hasUserSubmittedFitFeedback = await getHasUserSubmittedFitFeedback();
    if (!hasUserSubmittedFitFeedback) {
      setIsFitFeedbackFormWindowVisible(true);
      if (fitFeedbackData.length < 5) {
        handleFitClose();
      }
    }
  };

  const logInBeforeFitFeedbackForm = () => {
    openFitFeedbackForm.current = true;
    storeDispatch(
      triggerLoginBeforeAction({
        title: t(`${TP}.logInBeforeFitFeedbackForm`, 'Log In to Continue'),
        darkTheme: false,
      }),
    );
  };

  useEffect(() => {
    if (isAuthenticated && openFitFeedbackForm.current) {
      void handleOpenFitFeedbackForm();
    }
  }, [openFitFeedbackForm.current, isAuthenticated]);

  const handleCloseFitFeedbackForm = () => {
    setIsFitFeedbackFormWindowVisible(false);
    dispatch({ type: FitFeedbackFormActions.resetForm });
    setErrorMessage(null);
  };

  const handleProvideFeedbackClick = async () => {
    sendTrackingEvent('PDP_FIT_PROVIDE_FEEDBACK_TAP', {
      product_template_slug: slug,
    });

    if (!isAuthenticated) {
      logInBeforeFitFeedbackForm();
    } else {
      await handleOpenFitFeedbackForm();
    }
  };

  const createFitFeedback = async () => {
    try {
      if (isSneakersFormState(state)) {
        await requestService({
          url: `/web-api/fit-feedback/create-fit-feedback`,
          method: 'POST',
          data: JSON.stringify({
            bodyShape: BODY_SHAPES.UNSET,
            fitsLike: state.sneakersFit,
            isSneaker: isSneakers,
            productTemplateSlug: slug,
            sizePurchased: state.sneakersSizePurchased,
            userId: user?.id?.toString(),
          }),
        });
      }

      if (isApparelFormState(state)) {
        await requestService({
          url: `/web-api/fit-feedback/create-fit-feedback`,
          method: 'POST',
          data: JSON.stringify({
            bodyShape: state.bodyShape,
            heightInches: getHeightInches({
              cm: Number(state.heightCm),
              feet: Number(state.heightFeet),
              inches: Number(state.heightInches),
              measurement: state.heightMeasurement,
            }),
            fitsLike: state.sizeFit,
            isSneaker: isSneakers,
            productTemplateSlug: slug,
            sizePurchased: state.sizePurchased,
            style: state.style,
            userId: user?.id?.toString(),
          }),
        });
      }

      setIsFitFeedbackFormWindowVisible(false);
    } catch {
      setErrorMessage(t(`${TP}.generalError`, 'Something went wrong. Please try again.'));
    }
  };

  const handleFormSubmit = async () => {
    sendTrackingEvent('PDP_FIT_SUBMIT_FEEDBACK_TAP', {
      product_template_slug: slug,
    });

    if (isAuthenticated) {
      await createFitFeedback();
      if (!errorMessage) {
        handleFitClick();
      }
    }
  };

  useEffect(() => {
    handleCloseFitFeedbackForm();
    openFitFeedbackForm.current = false;
  }, [slug]);

  const values = {
    errorMessage,
    fitWindowState,
    getHasUserSubmittedFitFeedback,
    handleCloseFitFeedbackForm,
    handleFitClick,
    handleFitClose,
    handleFitMouseEnter,
    handleFitMouseLeave,
    handleFormSubmit,
    handleProvideFeedbackClick,
    isApparelFitFeedbackEnabled,
    isFitFeedbackFormWindowVisible,
    isSneakersFitFeedbackEnabled: isSneakers,
    setFitFeedbackData,
    setIsFitFeedbackFormWindowVisible,
  };

  return (
    <FitFeedbackContext.Provider value={values}>
      <FitFeedbackFormStateContext.Provider value={state}>
        <FitFeedbackFormDispatchContext.Provider value={dispatch}>
          {children}
        </FitFeedbackFormDispatchContext.Provider>
      </FitFeedbackFormStateContext.Provider>
    </FitFeedbackContext.Provider>
  );
};
